import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { NotificationsTable } from '@backstage/plugin-notifications';
import { makeStyles } from '@material-ui/core/styles';
import { NotificationsEmptyState } from './NotificationsEmptyState';
import { NotificationsFilterPanel } from './NotificationsFilterPanel';
import { useNotificationsFeed } from './useNotificationsFeed';
import type { NotificationsView } from './types';

/*
 * The notifications page, replacing the stock one.
 *
 * The stock page renders an unlabelled filters column and a bare table on the
 * app canvas. This keeps the same two-up structure and the same vendor table —
 * which picks up the shared table treatment — but sets the page in the portal's
 * padded, titled page shell, cards the filters, and gives an empty inbox a
 * proper empty state instead of a "No records to display" row.
 */
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '32px 40px 40px 40px',
    [theme.breakpoints.down('xs')]: { gap: 16, padding: '20px 20px 32px' },
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--bui-fg-primary)',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 13,
    color: 'var(--bui-fg-secondary)',
  },
  /*
   * Two-up on a desktop, stacked on a phone: 260px of filter rail beside the
   * table leaves it 23px of column at 402px.
   */
  body: {
    display: 'grid',
    gridTemplateColumns: '260px minmax(0, 1fr)',
    alignItems: 'start',
    gap: 24,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 16,
    },
  },
}));

const TABLE_TITLES: Record<NotificationsView, string> = {
  unread: 'Unread notifications',
  read: 'Read notifications',
  saved: 'Saved notifications',
  all: 'All notifications',
};

export function NotificationsPage() {
  const classes = useStyles();
  const {
    filters,
    onFiltersChange,
    page,
    setPage,
    pageSize,
    setPageSize,
    onSearchChange,
    onUpdate,
    loading,
    error,
    notifications,
    totalCount,
    isUnread,
    allTopics,
    isUnfiltered,
    hasSearchText,
  } = useNotificationsFeed();

  const showEmptyState =
    !loading && !error && totalCount === 0 && !hasSearchText;

  const body = () => {
    if (error) {
      return <ResponseErrorPanel error={error} />;
    }
    if (loading && !notifications) {
      return <Progress />;
    }
    if (showEmptyState) {
      return <NotificationsEmptyState isUnfiltered={isUnfiltered} />;
    }
    return (
      <NotificationsTable
        title={`${TABLE_TITLES[filters.view]} (${totalCount})`}
        isLoading={loading}
        isUnread={isUnread}
        notifications={notifications}
        onUpdate={onUpdate}
        setContainsText={onSearchChange}
        onPageChange={setPage}
        onRowsPerPageChange={setPageSize}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
      />
    );
  };

  return (
    <div className={classes.root}>
      <header>
        <h1 className={classes.title}>Notifications</h1>
        <p className={classes.subtitle}>
          Announcements, incidents and portal activity addressed to you.
        </p>
      </header>
      <div className={classes.body}>
        <NotificationsFilterPanel
          filters={filters}
          onChange={onFiltersChange}
          allTopics={allTopics}
        />
        <div>{body()}</div>
      </div>
    </div>
  );
}
